import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { meetingEndpoints } from '../../services/api/endpoints/meeting.endpoint';
import { MeetingProviderStatus } from '../../types/meeting';

export const useMeetingProviders = () => {
  const [providers, setProviders] = useState<MeetingProviderStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );
  const orgId = selectedOrganization?.id;

  const fetchProviders = useCallback(async () => {
    if (!orgId) return;
    setIsLoading(true);
    try {
      const result = await meetingEndpoints.getMeetingProviders(orgId);
      setProviders(result.data || []);
    } catch (error) {
      console.error('Failed to fetch meeting providers:', error);
      setProviders([]);
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const openAuthPopup = (url: string, name: string) => {
    const popup = window.open(url, name, 'width=500,height=600,resizable,scrollbars=yes,status=1');
    if (!popup) {
      alert('Popup blocked! Please allow popups and try again.');
      return;
    }

    // COOP headers on OAuth pages block popup.closed checks,
    // so we refetch providers when this window regains focus instead.
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      fetchProviders();
    };
    window.addEventListener('focus', onFocus);
  };

  const connectZoom = async () => {
    if (!orgId) return;
    try {
      const url = await meetingEndpoints.getZoomAuthUrl(orgId);
      if (!url) throw new Error('Failed to get Zoom auth URL');
      openAuthPopup(url, 'ZoomAuthPopup');
    } catch (error) {
      console.error('Failed to connect Zoom:', error);
      throw error;
    }
  };

  const connectGoogleMeet = async () => {
    if (!orgId) return;
    try {
      const url = await meetingEndpoints.getGoogleMeetAuthUrl(orgId);
      if (!url) throw new Error('Failed to get Google Meet auth URL');
      openAuthPopup(url, 'GoogleMeetAuthPopup');
    } catch (error) {
      console.error('Failed to connect Google Meet:', error);
      throw error;
    }
  };

  const disconnect = async (providerName: string) => {
    if (!orgId) return;
    try {
      await meetingEndpoints.disconnectProvider(orgId, providerName);
      await fetchProviders();
    } catch (error) {
      console.error(`Failed to disconnect ${providerName}:`, error);
      throw error;
    }
  };

  const isZoomConnected = providers.some(p => p.providerName === 'zoom');
  const isGoogleMeetConnected = providers.some(p => p.providerName === 'google_meet');

  const getProviderEmail = (providerName: string) => {
    return providers.find(p => p.providerName === providerName)?.accountEmail || '';
  };

  return {
    providers,
    isLoading,
    isZoomConnected,
    isGoogleMeetConnected,
    getProviderEmail,
    connectZoom,
    connectGoogleMeet,
    disconnect,
    refetch: fetchProviders,
  };
};
